import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import React, { useState } from "react";
import { DayPicker, DayPickerProps } from "react-day-picker";
import { cn } from "utils";
import log from "logbeacon";
import { TimePicker } from "./TimePicker";
import Translations from "@translate/components/DatePicker/DatePicker";
import { I18nUtil } from "@utils/i18n";
import { useIsPc } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

export interface DatePickerProps {
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
  // className?: string;
  // pickerClassName?: string;
  // dayPickerClassName?: string;
  classNames?: {
    container?: string;
    picker?: string;
    dayPicker?: string;
  };
  disabled?: DayPickerProps["disabled"];
  showTime?: boolean;
  onConfirm?: (date: Date | undefined) => void;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  selected,
  onSelect,
  classNames,
  disabled,
  showTime = false,
  onConfirm,
}) => {
  /**
   * 内部维护的日期时间：
   * - showTime = false 时，仅由外部 selected 控制，不参与回调
   * - showTime = true 时，用于暂存「待确认」的日期时间，只在点击 Confirm 时回调给外部
   */
  const [internalDateTime, setInternalDateTime] = useState<Date | undefined>(
    selected,
  );
  const [isPc] = useIsPc();
  const isMobile = !isPc;
  const [showTimePanelOnMobile, setShowTimePanelOnMobile] = useState(false);

  const handleDateSelect = (date: Date | undefined) => {
    // 不展示时间时，直接把选择结果抛给外部
    if (!showTime) {
      log.debug("[文案]date", date);
      onSelect?.(date);
      return;
    }

    // showTime = true 时，只更新内部的日期时间，不直接回调外部
    if (!date) {
      setInternalDateTime(undefined);
      return;
    }

    setInternalDateTime((prev) => {
      // 如果之前已经有时间，则保留时间，只替换年月日；否则默认 00:00:00
      const hours = prev?.getHours() ?? 0;
      const minutes = prev?.getMinutes() ?? 0;
      const seconds = prev?.getSeconds() ?? 0;

      const next = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        hours,
        minutes,
        seconds,
      );
      return next;
    });

    // 移动端场景下，选择日期后切换到时间选择视图
    if (isMobile) {
      setShowTimePanelOnMobile(true);
    }
  };

  const handleTimeChange = (next: Date) => {
    if (!showTime) {
      return;
    }
    // TimePicker 只负责更新时间，真正回调仍然由 Confirm 按钮触发
    setInternalDateTime(next);
  };

  const handleConfirm = () => {
    log.debug("[DatePicker] confirm datetime", internalDateTime);
    onConfirm?.(internalDateTime);
    if (!showTime) {
      // 理论上不会走到这里，兜底处理
      onSelect?.(selected);
      return;
    }
    onSelect?.(internalDateTime);
  };

  const handleBackToDayOnMobile = () => {
    if (!showTime) {
      return;
    }
    setShowTimePanelOnMobile(false);
  };

  const dayPickerSelected = showTime
    ? (internalDateTime ?? selected)
    : selected;
  const dayPickerDefaultMonth = showTime
    ? (internalDateTime ?? selected)
    : selected;

  const dayPickerNode = (
    <DayPicker
      selected={dayPickerSelected}
      defaultMonth={dayPickerDefaultMonth}
      mode="single"
      onDayClick={handleDateSelect}
      disabled={disabled}
      showOutsideDays={false}
      weekStartsOn={0}
      formatters={{
        formatWeekdayName: (date) => {
          const labelsSunFirst = [
            "Sun",
            "Mon",
            "Tue",
            "Wed",
            "Thu",
            "Fri",
            "Sat",
          ];
          return labelsSunFirst[date.getDay()];
        },
      }}
      className={cn("rdp-custom", classNames?.dayPicker)}
      classNames={{
        months: "flex flex-col space-y-4 relative",
        caption: "flex justify-between items-center px-2 mb-4",
        caption_label: "title_m text-content_primary",
        nav: "flex items-center absolute top-0 right-0 gap-3",
        nav_button: cn(
          "inline-flex items-center justify-center",
          "h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100",
          "text-gray-300 hover:text-content_primary hover:bg-[#333333] rounded-lg transition-all",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        month_grid: "w-full",
        head_row: "flex mb-2",
        head_cell:
          "text-gray-400 rounded-md w-9 font-medium text-xs text-center uppercase tracking-wide",
        row: "flex w-full mt-[10px]",
        cell: cn(
          "relative text-center text-sm focus-within:relative focus-within:z-20",
        ),
        day: cn("py-[5px] px-[6px] max-md:text-center"),
        day_button: cn(
          "body_medium_m w-6 h-6 text-center",
          "rounded-1 text-gray-300 transition-all",
          "hover:bg-background_brand_overlay10 hover:text-content_brand_primary",
          "focus:bg-background_brand_overlay10 focus:text-content_brand_primary",
        ),
        day_selected: cn(
          "bg-background_brand_overlay10 text-content_brand_primary",
        ),
        weekday: "body_s p-1 text-content_primary",
        button_previous: "flex",
        button_next: "flex",
        month_caption:
          "border-b border-border_white_transparent10 mb-[10px] pb-[10px]",
      }}
      components={{
        Chevron: ({ orientation }) => (
          <div className="w-5 h-5 p-1 flex items-center justify-center">
            <OspIcons
              name="chevron-left"
              size={12}
              className={cn("text-content_primary", {
                "rotate-180": orientation === "right",
              })}
            />
          </div>
        ),
      }}
    />
  );

  // Web / PC 布局：日历 +（可选）右侧时间 + 底部 Confirm
  const webLayout = (
    <div
      className={cn(
        "bg-background_solid_tertiary border border-border_white_transparent10 rounded-xl p-4 w-fit shadow-lg flex-col justify-items-end max-md:flex",
        classNames?.container,
      )}
    >
      <div className={cn(showTime && "flex gap-4", classNames?.picker)}>
        {dayPickerNode}
        {showTime && (
          <div className="border-l border-border_white_transparent10 pl-4">
            <TimePicker
              date={internalDateTime ?? selected}
              onChange={handleTimeChange}
              disabled={disabled}
            />
          </div>
        )}
      </div>
      {showTime && (
        <button
          aria-label="Date Picker"
          data-openreplay-label={`Date Picker Button | ${I18n.t("Confirm")}`}
          id="btn-date-picker"
          className={cn(
            "button_s text-content_inverse_primary mt-4",
            "primary_button xs_button",
            "bg-content_primary text-content_inverse_primary",
          )}
          onClick={handleConfirm}
        >
          {I18n.t("Confirm")}
        </button>
      )}
    </div>
  );

  // 移动端布局（仅在 showTime=true 且为移动端时启用）：
  // - 第一步：选择日期（只展示日历）
  // - 第二步：选择时间（只展示时间 + 顶部返回 + Confirm 按钮）
  if (showTime && isMobile) {
    return (
      <div
        className={cn(
          "bg-background_solid_tertiary border border-border_white_transparent10 rounded-xl p-4 w-full shadow-lg flex-col justify-items-end flex",
          classNames?.container,
        )}
      >
        <div className="flex flex-col">
          {showTimePanelOnMobile && (
            <div className="flex items-center mb-4">
              <button
                aria-label="Date Picker"
                data-openreplay-label="Date Picker Button"
                id="btn-date-picker-2"
                type="button"
                className="flex items-center justify-center rounded-full hover:bg-background_white_overlay10"
                onClick={handleBackToDayOnMobile}
              >
                <OspIcons
                  name="chevron-left"
                  size={16}
                  className="text-content_primary"
                />
              </button>
              <span className="body_m text-content_primary">
                {I18n.t("Back")}
              </span>
            </div>
          )}

          {!showTimePanelOnMobile && (
            <div className={cn(classNames?.picker)}>{dayPickerNode}</div>
          )}

          {showTimePanelOnMobile && (
            <>
              <div className="mb-4">
                <TimePicker
                  date={internalDateTime ?? selected}
                  onChange={handleTimeChange}
                  disabled={disabled}
                />
              </div>
              <button
                aria-label="Date Picker"
                data-openreplay-label={`Date Picker Button | ${I18n.t("Confirm")}`}
                id="btn-date-picker-3"
                type="button"
                className={cn(
                  "button_s text-content_inverse_primary",
                  "primary_button xs_button",
                  "bg-content_primary text-content_inverse_primary",
                )}
                onClick={handleConfirm}
              >
                {I18n.t("Confirm")}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // 非移动端场景或不展示时间时，保持原有 Web 布局
  return webLayout;
};
